import React, { FunctionComponent, useState } from 'react';
import { Button, Icon, Modal, Popup } from 'semantic-ui-react';
import { Review } from '../types/review.type';
import { ReviewPresentationsForm } from './ReviewPresentationsForm/ReviewPresentationsForm';

/** A company with several presentations: a review can be visible on more than one */
export const hasSeveralCompanyPresentations = (review: Review): boolean =>
  (review.presentations[0]?.presentation.company?.proPresentationsAggregate
    .aggregate?.count ?? 0) > 1;

type ReviewPresentationsButtonProps = {
  review: Review;
  onReviewChange: () => void;
  /** "Modifier" next to the presentations of the review, or an icon among its actions */
  variant?: 'text' | 'icon';
};

export const ReviewPresentationsButton: FunctionComponent<
  ReviewPresentationsButtonProps
> = ({ review, onReviewChange, variant = 'icon' }) => {
  const [isOpen, setIsOpen] = useState(false);

  // A refused or spam review is shown nowhere, a copy left between two companies keeps its own
  // presentation, and most companies have a single presentation: nothing to choose
  if (
    review.status === 'ko' ||
    review.isSpam ||
    review.originalProReviewId !== null ||
    !hasSeveralCompanyPresentations(review)
  ) {
    return null;
  }

  // Only a published review can be visible on other presentations: a review awaiting moderation
  // shows the action, disabled until it is published
  const isPublished = review.status === 'ok';

  return (
    <>
      <Popup
        content={
          isPublished
            ? "Choisir les fiches de l'avis"
            : "Choisir les fiches de l'avis : disponible une fois l'avis publié"
        }
        position="top center"
        // A disabled button gets no mouse events: the popup is on its wrapper
        trigger={
          <span style={{ display: 'inline-block' }}>
            {variant === 'text' ? (
              <Button
                basic
                compact
                size="mini"
                disabled={!isPublished}
                onClick={(): void => setIsOpen(true)}
              >
                Modifier
              </Button>
            ) : (
              <Button
                basic
                color="grey"
                icon
                disabled={!isPublished}
                onClick={(): void => setIsOpen(true)}
              >
                <Icon name="clone outline" />
              </Button>
            )}
          </span>
        }
      />
      <Modal open={isOpen} onClose={(): void => setIsOpen(false)} size="small">
        <ReviewPresentationsForm
          review={review}
          onCompleted={(): void => {
            setIsOpen(false);
            onReviewChange();
          }}
        />
      </Modal>
    </>
  );
};
